import type { ReactNode } from 'react';
import {
  Building2,
  Calendar,
  Car,
  FilePen,
  Fuel,
  Gauge,
  Heart,
  Palette,
  Settings,
} from 'lucide-react';

interface VehicleDetailOverviewProps {
  vehicle: any;
  config: any;
  vehicleStore: any;
  availableStoresCount: number;
  stores: any[];
  formatMileage: (mileage: number) => string;
}

export function VehicleDetailOverview({
  vehicle,
  config,
  vehicleStore,
  availableStoresCount,
  stores,
  formatMileage,
}: VehicleDetailOverviewProps) {
  const storeForVehicle = stores?.find((s: any) => s._id === vehicle.store);

  const items: { icon: ReactNode; label: string; value: string }[] = [];

  if (vehicle.manufactureYear || vehicle.modelYear) {
    items.push({
      icon: <Calendar className="h-4 w-4" />,
      label: 'Ano',
      value: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
    });
  }

  if (vehicle.mileage !== undefined && vehicle.mileage !== null) {
    items.push({
      icon: <Gauge className="h-4 w-4" />,
      label: 'KM',
      value: formatMileage(vehicle.mileage),
    });
  }

  if (vehicle.fuel) {
    items.push({ icon: <Fuel className="h-4 w-4" />, label: 'Combustível', value: vehicle.fuel });
  }

  if (vehicle.type !== 2 && vehicle.transmission) {
    items.push({ icon: <Settings className="h-4 w-4" />, label: 'Câmbio', value: vehicle.transmission });
  }

  if (vehicle.color) {
    items.push({ icon: <Palette className="h-4 w-4" />, label: 'Cor', value: vehicle.color });
  }

  if (vehicle.type !== 2 && vehicle.doors) {
    items.push({ icon: <Car className="h-4 w-4" />, label: 'Portas', value: String(vehicle.doors) });
  }

  if (availableStoresCount > 1 && storeForVehicle) {
    items.push({
      icon: <Building2 className="h-4 w-4" />,
      label: 'Loja',
      value: storeForVehicle.company,
    });
  }

  const showStats =
    vehicle.statistics &&
    ((vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals) ||
      (vehicle.statistics.favorites && vehicleStore?.ad?.displayNumberOfInterestedCustomers));

  return (
    <section className="wl6-vd-section">
      <div className="wl6-vd-section__head">
        <p className="wl6-eyebrow">Detalhes</p>
        <h2 className="wl6-vd-section__title wl6-h2">Visão geral</h2>
      </div>

      <div className="wl6-vd-overview">
        {items.map((item) => (
          <div key={item.label} className="wl6-vd-overview__item">
            <div className="wl6-vd-overview__icon">{item.icon}</div>
            <div>
              <p className="wl6-vd-overview__label">{item.label}</p>
              <p className="wl6-vd-overview__value">{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      {showStats && (
        <div className="wl6-vd-stats">
          {vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
            <div className="wl6-vd-stats__item">
              <FilePen className="h-4 w-4" />
              <span>
                {vehicle.statistics.financing > 1
                  ? `${vehicle.statistics.financing} propostas em análise`
                  : `${vehicle.statistics.financing} proposta em análise`}
              </span>
            </div>
          )}
          {vehicle.statistics.favorites && vehicleStore?.ad?.displayNumberOfInterestedCustomers && (
            <div className="wl6-vd-stats__item">
              <Heart className="h-4 w-4" />
              <span>
                {vehicle.statistics.favorites > 1
                  ? `${vehicle.statistics.favorites} clientes interessados`
                  : `${vehicle.statistics.favorites} cliente interessado`}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
